import React, { useEffect, useState } from 'react';
import { InternalFieldName, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Card from '../Card/Card';
import api from '../../services/api';
import { useAuth } from '../../contexts/auth';
import { InvoiceViewModel } from '../../models/Invoice/InvoiceViewModel';
import InvoiceStatusState from '../../enums/InvoiceStatusState';

interface InvoiceInfoProps {
  invoice: InvoiceViewModel;
}

interface InvoiceInfoFormFields {
  invoiceInfo1: string;
  invoiceInfo2: string;
  isPayingJustTheHalf: boolean;
  payrollBonus: number;
}

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export default function InvoiceInfo({ invoice }: InvoiceInfoProps) {
  const {
    user: { organization },
  } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<InvoiceInfoFormFields>({ mode: 'onBlur' });

  const [formErrors, setFormErrors] = useState<Record<string, any>>([]);
  const [isReadOnly, setIsReadOnly] = useState(true);

  const onSubmit = async (data: InvoiceInfoFormFields) => {
    try {
      const { payrollBonus, ...rest } = data;
      await api.post(
        `organizations/${organization.id}/invoices/${invoice.id}/info`,
        {
          payrollBonus: !payrollBonus ? 0 : payrollBonus,
          ...rest,
        }
      );

      toast.success('Informações registradas com sucesso');
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  useEffect(() => {
    setIsReadOnly(
      invoice.statusState === InvoiceStatusState.Draft ||
        invoice.statusState === InvoiceStatusState.Canceled ||
        invoice.statusState === InvoiceStatusState.Completed
    );

    reset({
      invoiceInfo1: invoice.invoiceInfo1,
      invoiceInfo2: invoice.invoiceInfo2,
      isPayingJustTheHalf: invoice.isPayingJustTheHalf,
      payrollBonus: invoice.payrollBonus,
    });
  }, [invoice]);

  return (
    <Card header='Informações da OS' isLoading={false}>
      <form
        style={{
          paddingTop: '1rem',
          paddingBottom: '1rem',
        }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor='inputLabel' className='form-label fw-bold'>
            Pedido de compra:
          </label>
          <input
            {...register('invoiceInfo1')}
            className='form-control'
            placeholder='Pedido de compra'
            type='text'
          />
          {errors.invoiceInfo1 && (
            <div className='form-text text-danger'>
              {errors.invoiceInfo1.message}
            </div>
          )}
        </div>
        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor='inputLabel' className='form-label fw-bold'>
            Nota fiscal:
          </label>
          <input
            {...register('invoiceInfo2')}
            className='form-control'
            placeholder='Nota fiscal'
            type='text'
          />
          {errors.invoiceInfo2 && (
            <div className='form-text text-danger'>
              {errors.invoiceInfo2.message}
            </div>
          )}
        </div>
        <div className='col-sm-12 mt-3'>
          <div className='form-check form-switch'>
            <input
              className='form-check-input'
              type='checkbox'
              role='switch'
              id='isPayingJustTheHalf'
              {...register('isPayingJustTheHalf')}
              disabled={isReadOnly}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label className='form-check-label' htmlFor='isPayingJustTheHalf'>
              Colaborador 50%
            </label>
          </div>
        </div>
        <div className='input-group h-75 input-group-sm mt-3'>
          <span className='input-group-text'>R$</span>
          <input
            {...register('payrollBonus')}
            className='form-control'
            placeholder='Bônus'
            type='number'
            step='.01'
            disabled={isReadOnly}
          />
        </div>
        <div className='text-end mt-3'>
          <button type='submit' className='btn btn-primary'>
            Salvar
          </button>
        </div>
      </form>
    </Card>
  );
}
